import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { useEquipment } from '../../../context/EquipmentContext';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { INSPECTION_ROUNDS } from '../../../data/nurseAppModels';

const InspectionApprovalDashboard = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const { approveInspection, rejectInspection } = useEquipment();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTab, setFilterTab] = useState('pending'); // pending, approved, rejected, all
  const [selectedInspections, setSelectedInspections] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    const loadInspections = async () => {
      if (!currentTeam) {
        setLoading(false);
        return;
      }

      // Check if user is Leader or Deputy Leader
      const userTeamRole = currentTeam.userRole;
      if (userTeamRole !== 'LEADER' && userTeamRole !== 'DEPUTY_LEADER') {
        alert('คุณไม่มีสิทธิ์เข้าถึงหน้านี้');
        navigate('/nurse/equipment');
        return;
      }

      try {
        setLoading(true);
        let inspectionsQuery = query(
          collection(db, 'equipmentInspections'),
          where('teamId', '==', currentTeam.id),
          orderBy('submittedAt', 'desc')
        );

        const snapshot = await getDocs(inspectionsQuery);
        const inspectionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setInspections(inspectionsData);
      } catch (err) {
        console.error('Error loading inspections:', err);
        // Fallback: query without orderBy if index doesn't exist
        try {
          const fallbackQuery = query(
            collection(db, 'equipmentInspections'),
            where('teamId', '==', currentTeam.id)
          );
          const snapshot = await getDocs(fallbackQuery);
          const inspectionsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          // Sort in memory
          inspectionsData.sort((a, b) => {
            const dateA = a.submittedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(a.createdAt);
            const dateB = b.submittedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(b.createdAt);
            return dateB - dateA;
          });
          setInspections(inspectionsData);
        } catch (fallbackErr) {
          console.error('Error loading inspections (fallback):', fallbackErr);
        }
      } finally {
        setLoading(false);
      }
    };

    loadInspections();
  }, [currentTeam, navigate]);

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: { bg: 'bg-gray-100', text: 'text-gray-800', label: '📝 Draft', icon: '📝' },
      PENDING_APPROVAL: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '⏳ รออนุมัติ', icon: '⏳' },
      APPROVED: { bg: 'bg-green-100', text: 'text-green-800', label: '✅ อนุมัติแล้ว', icon: '✅' },
      REJECTED: { bg: 'bg-red-100', text: 'text-red-800', label: '❌ ไม่อนุมัติ', icon: '❌' }
    };
    return badges[status] || badges.DRAFT;
  };

  const getRoundLabel = (round) => {
    const rounds = {
      [INSPECTION_ROUNDS.MORNING_BEFORE]: 'เช้า - ก่อนเข้าเวร',
      [INSPECTION_ROUNDS.MORNING_AFTER]: 'เช้า - หลังเข้าเวร',
      [INSPECTION_ROUNDS.AFTERNOON_BEFORE]: 'บ่าย - ก่อนเข้าเวร',
      [INSPECTION_ROUNDS.AFTERNOON_AFTER]: 'บ่าย - หลังเข้าเวร',
      [INSPECTION_ROUNDS.NIGHT_BEFORE]: 'ดึก - ก่อนเข้าเวร',
      [INSPECTION_ROUNDS.NIGHT_AFTER]: 'ดึก - หลังเข้าเวร'
    };
    return rounds[round] || round;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredInspections = (() => {
    switch (filterTab) {
      case 'pending':
        return inspections.filter(i => i.status === 'PENDING_APPROVAL');
      case 'approved':
        return inspections.filter(i => i.status === 'APPROVED');
      case 'rejected':
        return inspections.filter(i => i.status === 'REJECTED');
      default:
        return inspections;
    }
  })();

  const handleSelectInspection = (inspectionId) => {
    setSelectedInspections(prev => 
      prev.includes(inspectionId)
        ? prev.filter(id => id !== inspectionId)
        : [...prev, inspectionId]
    );
  };

  const handleSelectAll = () => {
    const pendingIds = filteredInspections
      .filter(i => i.status === 'PENDING_APPROVAL')
      .map(i => i.id);
    
    if (selectedInspections.length === pendingIds.length) {
      setSelectedInspections([]);
    } else {
      setSelectedInspections(pendingIds);
    }
  };

  const handleBatchApprove = async () => {
    if (selectedInspections.length === 0) {
      alert('กรุณาเลือกรายการที่ต้องการอนุมัติ');
      return;
    }

    if (!window.confirm(`ต้องการอนุมัติ ${selectedInspections.length} รายการหรือไม่?`)) {
      return;
    }

    try {
      setIsProcessing(true);
      const results = await Promise.all(
        selectedInspections.map(id => approveInspection(id))
      );

      const successCount = results.filter(r => r.success).length;
      const failCount = results.length - successCount;

      if (failCount > 0) {
        alert(`อนุมัติสำเร็จ ${successCount} รายการ, ล้มเหลว ${failCount} รายการ`);
      } else {
        alert(`อนุมัติสำเร็จ ${successCount} รายการ`);
      }

      setSelectedInspections([]);
      
      // Reload inspections
      const snapshot = await getDocs(
        query(
          collection(db, 'equipmentInspections'),
          where('teamId', '==', currentTeam.id)
        )
      );
      const inspectionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      inspectionsData.sort((a, b) => {
        const dateA = a.submittedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.submittedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA;
      });
      setInspections(inspectionsData);
    } catch (err) {
      console.error('Error batch approving:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('กรุณากรอกเหตุผลที่ไม่อนุมัติ');
      return;
    }

    try {
      setIsProcessing(true);
      const result = await rejectInspection(rejectingId, rejectionReason.trim());

      if (result.success) {
        alert('ปฏิเสธสำเร็จ');
        setShowRejectModal(false);
        setRejectingId(null);
        setRejectionReason('');
        
        // Reload inspections
        const snapshot = await getDocs(
          query(
            collection(db, 'equipmentInspections'),
            where('teamId', '==', currentTeam.id)
          )
        );
        const inspectionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        inspectionsData.sort((a, b) => {
          const dateA = a.submittedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.submittedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA;
        });
        setInspections(inspectionsData);
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error rejecting:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </div>
    );
  }

  const pendingCount = inspections.filter(i => i.status === 'PENDING_APPROVAL').length;
  const approvedCount = inspections.filter(i => i.status === 'APPROVED').length;
  const rejectedCount = inspections.filter(i => i.status === 'REJECTED').length;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/equipment')}
            className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
          >
            ← กลับ
          </button>
          <h1 className="text-2xl font-bold text-gray-800">อนุมัติผลการตรวจสอบ</h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-800">{pendingCount}</div>
            <div className="text-sm text-yellow-600">รออนุมัติ</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-800">{approvedCount}</div>
            <div className="text-sm text-green-600">อนุมัติแล้ว</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-800">{rejectedCount}</div>
            <div className="text-sm text-red-600">ไม่อนุมัติ</div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setFilterTab('pending');
                setSelectedInspections([]);
              }}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filterTab === 'pending'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              รออนุมัติ ({pendingCount})
            </button>
            <button
              onClick={() => {
                setFilterTab('approved');
                setSelectedInspections([]);
              }}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filterTab === 'approved'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              อนุมัติแล้ว ({approvedCount})
            </button>
            <button
              onClick={() => {
                setFilterTab('rejected');
                setSelectedInspections([]);
              }}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filterTab === 'rejected'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ไม่อนุมัติ ({rejectedCount})
            </button>
            <button
              onClick={() => {
                setFilterTab('all');
                setSelectedInspections([]);
              }}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filterTab === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ทั้งหมด ({inspections.length})
            </button>
          </div>
        </div>

        {/* Batch Actions (only for pending) */}
        {filterTab === 'pending' && filteredInspections.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedInspections.length === filteredInspections.length && filteredInspections.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">เลือกทั้งหมด</span>
                </label>
                {selectedInspections.length > 0 && (
                  <span className="text-sm text-gray-600">
                    เลือกแล้ว {selectedInspections.length} รายการ
                  </span>
                )}
              </div>
              {selectedInspections.length > 0 && (
                <button
                  onClick={handleBatchApprove}
                  disabled={isProcessing}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
                >
                  {isProcessing ? 'กำลังอนุมัติ...' : `อนุมัติ ${selectedInspections.length} รายการ`}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Inspections List */}
        {filteredInspections.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              {filterTab === 'pending' ? 'ไม่มีรายการรออนุมัติ' : `ไม่มีรายการสถานะ ${filterTab}`}
            </h3>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredInspections.map((inspection) => {
              const statusBadge = getStatusBadge(inspection.status);
              const isSelected = selectedInspections.includes(inspection.id);
              const isPending = inspection.status === 'PENDING_APPROVAL';

              return (
                <div
                  key={inspection.id}
                  className={`bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition ${
                    isSelected ? 'ring-2 ring-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    {isPending && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectInspection(inspection.id)}
                        className="mt-1 w-4 h-4"
                      />
                    )}
                    <div className="flex-1 ml-4">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-lg text-sm font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                          {statusBadge.label}
                        </span>
                        <span className="text-sm text-gray-600">
                          {getRoundLabel(inspection.round)}
                        </span>
                        <span className="text-sm text-gray-500">
                          โดย: {inspection.inspectedByName || '-'}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-gray-500">วันที่ตรวจสอบ</p>
                          <p className="text-sm font-medium text-gray-800">
                            {formatDate(inspection.date)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">จำนวนรายการ</p>
                          <p className="text-sm font-medium text-gray-800">
                            {inspection.totalItems || 0} รายการ
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">วันที่ส่ง</p>
                          <p className="text-sm font-medium text-gray-800">
                            {formatDate(inspection.submittedAt || inspection.createdAt)}
                          </p>
                        </div>
                      </div>

                      {inspection.status === 'REJECTED' && inspection.rejectionReason && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-xs text-red-600 font-semibold mb-1">เหตุผลที่ไม่อนุมัติ:</p>
                          <p className="text-sm text-red-800">{inspection.rejectionReason}</p>
                        </div>
                      )}

                      {inspection.status === 'APPROVED' && inspection.approvedAt && (
                        <div className="mt-4 text-sm text-gray-600">
                          <p>อนุมัติโดย: {inspection.approvedByName || '-'}</p>
                          <p>วันที่อนุมัติ: {formatDate(inspection.approvedAt)}</p>
                        </div>
                      )}
                    </div>

                    <div className="ml-4 flex flex-col gap-2">
                      <Link
                        to={`/nurse/equipment/inspections/${inspection.id}/review`}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium text-center"
                      >
                        ดูรายละเอียด
                      </Link>
                      {isPending && (
                        <>
                          <button
                            onClick={async () => {
                              if (!window.confirm('ต้องการอนุมัติรายการนี้หรือไม่?')) return;
                              try {
                                setIsProcessing(true);
                                const result = await approveInspection(inspection.id);
                                if (result.success) {
                                  alert('อนุมัติสำเร็จ');
                                  // Reload
                                  const snapshot = await getDocs(
                                    query(
                                      collection(db, 'equipmentInspections'),
                                      where('teamId', '==', currentTeam.id)
                                    )
                                  );
                                  const inspectionsData = snapshot.docs.map(doc => ({
                                    id: doc.id,
                                    ...doc.data()
                                  }));
                                  inspectionsData.sort((a, b) => {
                                    const dateA = a.submittedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(a.createdAt);
                                    const dateB = b.submittedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(b.createdAt);
                                    return dateB - dateA;
                                  });
                                  setInspections(inspectionsData);
                                } else {
                                  alert(`เกิดข้อผิดพลาด: ${result.error}`);
                                }
                              } catch (err) {
                                alert(`เกิดข้อผิดพลาด: ${err.message}`);
                              } finally {
                                setIsProcessing(false);
                              }
                            }}
                            disabled={isProcessing}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                          >
                            อนุมัติ
                          </button>
                          <button
                            onClick={() => {
                              setRejectingId(inspection.id);
                              setShowRejectModal(true);
                            }}
                            disabled={isProcessing}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50"
                          >
                            ไม่อนุมัติ
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Reject Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-bold text-gray-800 mb-4">ไม่อนุมัติผลการตรวจสอบ</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  เหตุผลที่ไม่อนุมัติ *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  placeholder="กรุณากรอกเหตุผลที่ไม่อนุมัติ..."
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleReject}
                  disabled={isProcessing || !rejectionReason.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
                >
                  {isProcessing ? 'กำลังส่ง...' : 'ยืนยัน'}
                </button>
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectingId(null);
                    setRejectionReason('');
                  }}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium disabled:opacity-50"
                >
                  ยกเลิก
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InspectionApprovalDashboard;



